export type MembershipTier = "FREE" | "INSIDER" | "CREATOR" | "LIFETIME_CREATOR";

export interface MembershipEntitlement {
  readonly tier: MembershipTier;
  readonly monthlyPriceMinorUsd: number;
  readonly freeForever: boolean;
  readonly fullAgentic: true;
  readonly bookingAccess: true;
  readonly fxFeeBasisPoints: 80 | 40 | 20;
  readonly kidsCornerIncludedDownloadsPerMonth: 0 | 10 | 100;
  readonly subscriptionReferralCommissionEligible: boolean;
  readonly bookingPurchaseOverrideEligible: boolean;
  readonly weeklyPayoutEligible: boolean;
  readonly minimumPayoutMinorUsd: 2500 | null;
  readonly hostLivestreams: boolean;
  readonly creatorMonetizationToolkit: "NONE" | "FULL";
  readonly lifetime: boolean;
}

export const MEMBERSHIP_ENTITLEMENTS: Readonly<Record<MembershipTier, MembershipEntitlement>> = Object.freeze({
  FREE: Object.freeze({
    tier: "FREE",
    monthlyPriceMinorUsd: 0,
    freeForever: true,
    fullAgentic: true,
    bookingAccess: true,
    fxFeeBasisPoints: 80,
    kidsCornerIncludedDownloadsPerMonth: 0,
    subscriptionReferralCommissionEligible: false,
    bookingPurchaseOverrideEligible: false,
    weeklyPayoutEligible: false,
    minimumPayoutMinorUsd: null,
    hostLivestreams: false,
    creatorMonetizationToolkit: "NONE",
    lifetime: false
  }),
  INSIDER: Object.freeze({
    tier: "INSIDER",
    monthlyPriceMinorUsd: 2500,
    freeForever: false,
    fullAgentic: true,
    bookingAccess: true,
    fxFeeBasisPoints: 40,
    kidsCornerIncludedDownloadsPerMonth: 10,
    subscriptionReferralCommissionEligible: true,
    bookingPurchaseOverrideEligible: true,
    weeklyPayoutEligible: true,
    minimumPayoutMinorUsd: 2500,
    hostLivestreams: false,
    creatorMonetizationToolkit: "NONE",
    lifetime: false
  }),
  CREATOR: Object.freeze({
    tier: "CREATOR",
    monthlyPriceMinorUsd: 5000,
    freeForever: false,
    fullAgentic: true,
    bookingAccess: true,
    fxFeeBasisPoints: 20,
    kidsCornerIncludedDownloadsPerMonth: 100,
    subscriptionReferralCommissionEligible: true,
    bookingPurchaseOverrideEligible: true,
    weeklyPayoutEligible: true,
    minimumPayoutMinorUsd: 2500,
    hostLivestreams: true,
    creatorMonetizationToolkit: "FULL",
    lifetime: false
  }),
  LIFETIME_CREATOR: Object.freeze({
    tier: "LIFETIME_CREATOR",
    monthlyPriceMinorUsd: 0,
    freeForever: false,
    fullAgentic: true,
    bookingAccess: true,
    fxFeeBasisPoints: 20,
    kidsCornerIncludedDownloadsPerMonth: 100,
    subscriptionReferralCommissionEligible: true,
    bookingPurchaseOverrideEligible: true,
    weeklyPayoutEligible: true,
    minimumPayoutMinorUsd: 2500,
    hostLivestreams: true,
    creatorMonetizationToolkit: "FULL",
    lifetime: true
  })
});

export function entitlementForTier(tier: MembershipTier): MembershipEntitlement {
  return MEMBERSHIP_ENTITLEMENTS[tier];
}

export function assertAgenticNotTierDegraded(): true {
  for (const entitlement of Object.values(MEMBERSHIP_ENTITLEMENTS)) {
    if (entitlement.fullAgentic !== true) throw new Error(`Agentic access is degraded for ${entitlement.tier}`);
  }
  return true;
}
